import type {
    FormElementFile,
    WorksheetAsset,
    WorksheetFormElement,
    WorksheetFormGetResponse,
} from '@packages/clients';

import type {
    WorksheetFormAssetEntity,
    WorksheetFormAssetMediaEntity,
    WorksheetFormElementEntity,
    WorksheetFormElementFileEntity,
    WorksheetFormElementOptionEntity,
    WorksheetFormEntity,
    WorksheetFormExistingElementEntity,
    WorksheetFormGridCellAnswerEntity,
    WorksheetFormGridColumnEntity,
    WorksheetFormGridRowEntity,
    WorksheetFormGridSubColumnEntity,
    WorksheetFormGridSubRowEntity,
    WorksheetFormSelectedOptionEntity,
    WorksheetFormUserAccountEntity,
} from '../entities';
import type { WorksheetFormDto } from './types';

/**
 * Map a {@link WorksheetFormElement} DTO to a {@link WorksheetFormElementEntity}.
 *
 * @param dto - the form element DTO
 * @returns the domain entity
 * @example
 * ```ts
 * const entity = toWorksheetFormElementEntity(element);
 * ```
 */
export function toWorksheetFormElementEntity(dto: WorksheetFormElement): WorksheetFormElementEntity {
    return {
        action: dto.action,
        answerAssets: dto.answerAssets?.map(toWorksheetFormAssetEntity),
        assets: dto.assets?.map(toWorksheetFormAssetEntity),
        formElementFile: dto.formElementFile ? toWorksheetFormElementFileEntity(dto.formElementFile) : undefined,
        grid: (dto.grid ?? []).map(toWorksheetFormGridRowEntity),
        hasDefaultValue: dto.worksheetElementDefaultValue,
        hasRule: dto.worksheetElementHasRule,
        isMultipleSelectionAllowed: dto.worksheetElementAllowMultiple,
        isRequired: dto.worksheetElementRequired,
        options: dto.options.map(toWorksheetFormElementOptionEntity),
        selectedOptions: dto.selectedOptions?.map(toWorksheetFormSelectedOptionEntity),
        worksheetElementDescription: dto.worksheetElementDescription ?? '',
        worksheetElementId: dto.worksheetElementId,
        worksheetElementLabel: dto.worksheetElementLabel,
        worksheetElementOrder: dto.worksheetElementOrder,
        worksheetElementPlaceholder: dto.worksheetElementPlaceholder,
        worksheetElementTooltip: dto.worksheetElementTooltip ?? '',
        worksheetElementTypeId: dto.worksheetElementTypeId,
        worksheetGridCellAnswers: dto.worksheetGridCellAnswers?.map(toWorksheetFormGridCellAnswerEntity),
    };
}

/**
 * Map a worksheet form DTO to a {@link WorksheetFormEntity}.
 * Used when fetching a worksheet form definition or retrieving an existing submission.
 *
 * @param dto - the worksheet form DTO from the backend
 * @returns the domain entity
 * @example
 * ```ts
 * const entity = toWorksheetFormEntity(response.data);
 * ```
 */
export function toWorksheetFormEntity(dto: WorksheetFormDto): WorksheetFormEntity {
    return {
        caseGroupName: dto.caseGroupName,
        caseType: dto.caseType,
        caseTypeId: dto.caseTypeId,
        createdAccount: toWorksheetFormUserAccountEntity(dto.createdAccount),
        createdTimestamp: dto.createdTimestamp,
        createdTimestampForDisplay: dto.createdTimestampForDisplay,
        description: dto.description,
        displayname: dto.displayname,
        elements: dto.elements.map(toWorksheetFormElementEntity),
        existingElements: (dto.existingElements ?? []).map(toWorksheetFormExistingElementEntity),
        groupStatus: dto.groupStatus,
        lastUpdatedAccount: toWorksheetFormUserAccountEntity(dto.lastUpdatedAccount),
        lastUpdatedTimestamp: dto.lastUpdatedTimestamp,
        lastUpdatedTimestampForDisplay: dto.lastUpdatedTimestampForDisplay,
        northstarId: dto.northstarId,
        oldWorksheetElements: dto.oldWorksheetElements?.map(toWorksheetFormElementEntity),
        version: dto.version,
        worksheetCaseGroupId: dto.worksheetCaseGroupId,
        worksheetGroupId: dto.worksheetGroupId,
        worksheetGroupName: dto.worksheetGroupName,
        worksheetId: dto.worksheetId,
        worksheetStatus: dto.worksheetStatus,
        worksheetSubmissionStatus: dto.worksheetSubmissionStatus,
    };
}

// ──────────────────────────────────────────────
// Internal mapper helpers (not exported)
// ──────────────────────────────────────────────

/**
 * Map a raw Asset DTO to a {@link WorksheetFormAssetEntity}.
 *
 * @param dto - the asset DTO
 * @returns the domain entity
 */
function toWorksheetFormAssetEntity(dto: WorksheetAsset): WorksheetFormAssetEntity {
    return {
        assetDispositionId: dto.assetDispositionId,
        assetDispositionIdDescription: dto.assetDispositionIdDescription,
        assetId: dto.assetId,
        assetProcessingStateId: dto.assetProcessingStateId,
        assetProcessingStateIdDescription: dto.assetProcessingStateIdDescription,
        assetTypeId: dto.assetTypeId,
        assetTypeIdDescription: dto.assetTypeIdDescription,
        caption: dto.caption,
        createdTimestamp: String(dto.createdTimestamp),
        createdTimestampDescription: dto.createdTimestampDescription,
        isImportedFromSiebel: dto.importedFromSiebel,
        media: (dto.media ?? []).map(toWorksheetFormAssetMediaEntity),
        name: dto.name,
        percentUploaded: dto.percentUploaded,
        percentUploadedDescription: dto.percentUploadedDescription,
    };
}

/**
 * Map a raw Media DTO to a {@link WorksheetFormAssetMediaEntity}.
 *
 * @param dto - the media DTO
 * @returns the domain entity
 */
function toWorksheetFormAssetMediaEntity(dto: WorksheetAsset['media'][number]): WorksheetFormAssetMediaEntity {
    return {
        assetFileId: dto.assetFileId,
        assetImageId: dto.assetImageId,
        assetImageTypeId: dto.assetImageTypeId,
        assetImageTypeIdDescription: dto.assetImageTypeIdDescription,
        assetVideoId: dto.assetVideoId,
        assetVideoTypeId: dto.assetVideoTypeId,
        contentType: dto.contentType,
        createdTimestamp: String(dto.createdTimestamp),
        createdTimestampDescription: dto.createdTimestampDescription,
        height: dto.height,
        heightDescription: dto.heightDescription,
        sizeInBytes: dto.sizeInBytes,
        sizeInBytesDescription: dto.sizeInBytesDescription,
        url: dto.url,
        width: dto.width,
        widthDescription: dto.widthDescription,
    };
}

/**
 * Map a {@link FormElementFile} to a {@link WorksheetFormElementFileEntity}.
 *
 * @param dto - the file DTO
 * @returns the domain entity
 */
function toWorksheetFormElementFileEntity(dto: FormElementFile): WorksheetFormElementFileEntity {
    return {
        assetId: dto.assetId,
        caption: dto.caption,
        file: dto.file,
        name: dto.name,
        overlay: dto.overlay ? { file: dto.overlay.file, name: dto.overlay.name, url: dto.overlay.url } : undefined,
        status: dto.status,
        url: dto.url,
    };
}

/**
 * Map a {@link WorksheetFormElementOptions} DTO to a {@link WorksheetFormElementOptionEntity}.
 *
 * @param dto - the element option DTO
 * @returns the domain entity
 */
function toWorksheetFormElementOptionEntity(
    dto: WorksheetFormElement['options'][number]
): WorksheetFormElementOptionEntity {
    return {
        answerText: dto.answerText,
        assets: dto.assets?.map(toWorksheetFormAssetEntity),
        isOther: dto.isOther,
        optionDisplayOrder: dto.optionDisplayOrder,
        optionFile: dto.optionFile ? toWorksheetFormElementFileEntity(dto.optionFile) : undefined,
        optionId: dto.optionId,
        optionLabel: dto.optionLabel,
        optionPlaceholder: dto.optionPlaceholder,
        optionValue: dto.optionValue,
        trgtElementIdList: dto.trgtElementIdList,
        trgtWorksheetElementIdList: dto.trgtWorksheetElementIdList,
    };
}

/**
 * Map an existing element DTO to a {@link WorksheetFormExistingElementEntity}.
 *
 * @param dto - the existing element DTO
 * @returns the domain entity
 */
function toWorksheetFormExistingElementEntity(
    dto: WorksheetFormGetResponse['existingElements'][number]
): WorksheetFormExistingElementEntity {
    return {
        elementId: dto.elementId,
        isActive: dto.isActive,
        mappingId: dto.mappingId,
        statusCode: dto.statusCode,
        worksheetId: dto.worksheetId,
    };
}

/**
 * Map a grid cell answer DTO to a {@link WorksheetFormGridCellAnswerEntity}.
 *
 * @param dto - the grid cell answer DTO
 * @returns the domain entity
 */
function toWorksheetFormGridCellAnswerEntity(
    dto: NonNullable<WorksheetFormElement['worksheetGridCellAnswers']>[number]
): WorksheetFormGridCellAnswerEntity {
    return {
        value: dto.value,
        worksheetElementGridId: dto.worksheetElementGridId,
    };
}

/**
 * Map a grid column DTO to a {@link WorksheetFormGridColumnEntity}.
 *
 * @param dto - the grid column DTO
 * @returns the domain entity
 */
function toWorksheetFormGridColumnEntity(
    dto: WorksheetFormElement['grid'][number]['columns'][number]
): WorksheetFormGridColumnEntity {
    return {
        columnLabel: dto.columnLabel,
        columnOrder: dto.columnOrder,
        defaultValue: dto.defaultValue,
        isRequired: dto.isRequired,
        subColumns: dto.subColumns?.map(toWorksheetFormGridSubColumnEntity),
        worksheetElementGridId: dto.worksheetElementGridId,
    };
}

/**
 * Map a grid row DTO to a {@link WorksheetFormGridRowEntity}.
 *
 * @param dto - the grid row DTO
 * @returns the domain entity
 */
function toWorksheetFormGridRowEntity(dto: WorksheetFormElement['grid'][number]): WorksheetFormGridRowEntity {
    return {
        columns: (dto.columns ?? []).map(toWorksheetFormGridColumnEntity),
        rowLabel: dto.rowLabel,
        rowOrder: dto.rowOrder,
        subRows: dto.subRows?.map(toWorksheetFormGridSubRowEntity),
    };
}

/**
 * Map a grid sub-column DTO to a {@link WorksheetFormGridSubColumnEntity}.
 *
 * @param dto - the grid sub-column DTO
 * @returns the domain entity
 */
function toWorksheetFormGridSubColumnEntity(
    dto: NonNullable<WorksheetFormElement['grid'][number]['columns'][number]['subColumns']>[number]
): WorksheetFormGridSubColumnEntity {
    return {
        columnOrder: dto.columnOrder,
        defaultValue: dto.defaultValue,
        isRequired: dto.isRequired,
        subColumnLabel: dto.subColumnLabel,
        value: dto.value,
        worksheetElementGridId: dto.worksheetElementGridId,
    };
}

/**
 * Map a grid sub-row DTO to a {@link WorksheetFormGridSubRowEntity}.
 *
 * @param dto - the grid sub-row DTO
 * @returns the domain entity
 */
function toWorksheetFormGridSubRowEntity(
    dto: NonNullable<WorksheetFormElement['grid'][number]['subRows']>[number]
): WorksheetFormGridSubRowEntity {
    return {
        columns: (dto.columns ?? []).map((col) => ({
            columnLabel: col.columnLabel,
            columnOrder: col.columnOrder,
            defaultValue: col.defaultValue,
            isRequired: col.isRequired,
            worksheetElementGridId: col.worksheetElementGridId,
        })),
        rowOrder: dto.rowOrder,
        subRowLabel: dto.subRowLabel,
    };
}

/**
 * Map a selected option DTO to a {@link WorksheetFormSelectedOptionEntity}.
 *
 * @param dto - the selected option DTO
 * @returns the domain entity
 */
function toWorksheetFormSelectedOptionEntity(
    dto: NonNullable<WorksheetFormElement['selectedOptions']>[number]
): WorksheetFormSelectedOptionEntity {
    return {
        answerText: dto.answerText,
        optionId: dto.optionId,
        optionLabel: dto.optionLabel,
    };
}

/**
 * Map a {@link WorksheetUsersAccount} DTO to a {@link WorksheetFormUserAccountEntity}.
 *
 * @param dto - the user account DTO
 * @returns the domain entity
 */
function toWorksheetFormUserAccountEntity(
    dto: WorksheetFormGetResponse['createdAccount']
): WorksheetFormUserAccountEntity {
    return {
        accountId: dto.accountId,
        accountStatusId: dto.accountStatusId,
        firstName: dto.firstName,
        lastName: dto.lastName,
        locale: dto.locale,
        name: dto.name,
        timeZone: dto.timeZone,
    };
}
